import React, { useState } from "react";
import ApiKeyRevealModal from "./ApiKeyRevealModal";
import ConfirmModal from "../../../../../../components/ConfirmModal";
import ManageChannelModal from "./ManageChannel/ManageChannelModal";
import { getApiUrl } from "../../../../../../config/api";
import { useAuth } from "../../../../../../firebase/AuthContext";

const DICT = {
    regenerateKey: { en: "Regenerate Key", zh: "重新產生金鑰" },
    regenerating: { en: "Regenerating…", zh: "重新產生中…" },
    regenerateErr: {
        en: "Failed to regenerate key.",
        zh: "重新產生金鑰失敗。",
    },
    deleteErr: { en: "Failed to delete channel.", zh: "刪除頻道失敗。" },
    products: { en: "products", zh: "個產品" },
    pins: { en: "pins", zh: "個綁定" },
    noOwner: { en: "No owner", zh: "無擁有者" },
    manage: { en: "Manage", zh: "管理" },
};

const t = (key, lang) => DICT[key]?.[lang] || DICT[key]?.en || key;

function ChannelCard({
    channel,
    language,
    currentUser,
    copiedId,
    onCopy,
    onDeleted,
}) {
    const [regenerating, setRegenerating] = useState(false);
    const [regenError, setRegenError] = useState(null);
    const [revealKey, setRevealKey] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [showManage, setShowManage] = useState(false);
    const { hasRole } = useAuth();
    const canManage = hasRole(["api.edit", "api.pins.manage"]);
    const canRotate = hasRole("api.rotate_key");
    const canDelete = hasRole("api.delete");

    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, variant, onConfirm }

    const handleRegenerate = () => {
        setConfirmModal({
            title: "Regenerate API Key?",
            message: `The current key for "${channel.channel_name}" will stop working immediately. You will be shown the new key once.`,
            confirmLabel: "Regenerate",
            variant: "warning",
            onConfirm: async () => {
                setConfirmModal(null);
                setRegenerating(true);
                setRegenError(null);
                try {
                    const token = await currentUser.getIdToken();
                    const res = await fetch(
                        `${getApiUrl()}/api/v2/channels/${channel.channel_id}/regenerate-key`,
                        {
                            method: "POST",
                            headers: { Authorization: `Bearer ${token}` },
                        },
                    );
                    const data = await res.json();
                    if (data.success) {
                        setRevealKey(data.data.api_key);
                    } else {
                        setRegenError(
                            data.error || t("regenerateErr", language),
                        );
                    }
                } catch (err) {
                    setRegenError(err.message || t("regenerateErr", language));
                } finally {
                    setRegenerating(false);
                }
            },
        });
    };

    const handleDeleteChannel = () => {
        setConfirmModal({
            title: "Delete Channel?",
            message: `"${channel.channel_name}" and its API key will be permanently deleted. This cannot be undone.`,
            confirmLabel: "Delete",
            variant: "danger",
            onConfirm: async () => {
                setConfirmModal(null);
                setDeleteLoading(true);
                setDeleteError(null);
                try {
                    const token = await currentUser.getIdToken();
                    const res = await fetch(
                        `${getApiUrl()}/api/v2/channels/${channel.channel_id}`,
                        {
                            method: "DELETE",
                            headers: { Authorization: `Bearer ${token}` },
                        },
                    );
                    const data = await res.json();
                    if (data.success) {
                        onDeleted?.(channel.channel_id);
                    } else {
                        setDeleteError(data.error || t("deleteErr", language));
                    }
                } catch (err) {
                    setDeleteError(err.message || t("deleteErr", language));
                } finally {
                    setDeleteLoading(false);
                }
            },
        });
    };

    return (
        <>
            {revealKey && (
                <ApiKeyRevealModal
                    language={language}
                    apiKey={revealKey}
                    channelName={channel.channel_name}
                    onClose={() => setRevealKey(null)}
                />
            )}
            {confirmModal && (
                <ConfirmModal
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    variant={confirmModal.variant}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
            {showManage && (
                <ManageChannelModal
                    channel={channel}
                    language={language}
                    currentUser={currentUser}
                    onClose={() => setShowManage(false)}
                />
            )}
            <div className="group relative bg-white border border-gray-200 rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/10 hover:-translate-y-1">
                {/* Hover gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none" />

                {/* Channel Header */}
                <div className="relative flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="min-w-0 flex-1">
                            <h3 className="text-lg font-bold text-gray-900 truncate group-hover:text-orange-500 transition-colors">
                                {channel.channel_name}
                            </h3>
                            <p className="text-xs text-gray-500">
                                ID: {channel.channel_id}
                            </p>
                        </div>
                    </div>
                    <span className="flex-shrink-0 px-2.5 py-1 bg-green-500/10 text-green-600 rounded-lg text-xs font-medium border border-green-500/20">
                        Active
                    </span>
                </div>

                {/* API Key Row */}
                <div className="relative mb-4 flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3 border border-gray-200">
                    <svg
                        className="w-4 h-4 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                        />
                    </svg>
                    <code className="text-sm text-gray-600 font-mono flex-1 truncate">
                        {channel.api_key}
                    </code>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                        <svg
                            className="w-4 h-4 text-gray-400 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                        </svg>
                        <span className="text-gray-600 truncate">
                            {channel.owner_id || (
                                <span className="text-gray-400 italic">
                                    {t("noOwner", language)}
                                </span>
                            )}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <svg
                            className="w-4 h-4 text-gray-400 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                        <span className="text-gray-600 text-xs">
                            {new Date(channel.created_at).toLocaleDateString()}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <svg
                            className="w-4 h-4 text-gray-400 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                            />
                        </svg>
                        <span className="text-gray-700 font-medium">
                            {channel.pinned_products}
                        </span>
                        <span className="text-gray-500 text-xs">
                            {t("products", language)}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <svg
                            className="w-4 h-4 text-gray-400 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                            />
                        </svg>
                        <span className="text-gray-700 font-medium">
                            {channel.pinned_rows}
                        </span>
                        <span className="text-gray-500 text-xs">
                            {t("pins", language)}
                        </span>
                    </div>
                </div>

                {/* Language Tags */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                    {channel.supported_languages.map((code) => (
                        <span
                            key={code}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-600"
                        >
                            <span className="font-mono">{code}</span>
                        </span>
                    ))}
                </div>

                {/* Action Buttons */}
                <div className="relative flex flex-col gap-2 pt-4 border-t border-gray-100">
                    {regenError && (
                        <p className="text-xs text-red-500 text-center">
                            {regenError}
                        </p>
                    )}
                    {deleteError && (
                        <p className="text-xs text-red-500 text-center">
                            {deleteError}
                        </p>
                    )}
                    <div className="flex items-center gap-2">
                        {canManage && (
                        <button
                            onClick={() => setShowManage(true)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-xl transition-all duration-200 border border-orange-200/50"
                        >
                            <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                            </svg>
                            {t("manage", language)}
                        </button>
                        )}
                        {canRotate && (
                        <button
                            onClick={handleRegenerate}
                            disabled={regenerating}
                            className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all duration-200 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <svg
                                className={`w-3.5 h-3.5 ${regenerating ? "animate-spin" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                            </svg>
                            {regenerating
                                ? t("regenerating", language)
                                : t("regenerateKey", language)}
                        </button>
                        )}
                        {canDelete && (
                        <button
                            onClick={handleDeleteChannel}
                            disabled={deleteLoading}
                            className="flex items-center justify-center p-2 text-sm text-red-500 bg-red-50 hover:bg-red-100 rounded-xl transition-all duration-200 border border-red-200/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <svg
                                className={`w-4 h-4 ${deleteLoading ? "animate-spin" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                {deleteLoading ? (
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                ) : (
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                )}
                            </svg>
                        </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChannelCard;
